import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import { getMerchantProfile, getMerchantSettings } from '../../services/merchant';

const BrandContext = createContext(null);

const DEFAULT_THEME = {
  primaryColor: '#1A7F6D',
  secondaryColor: '#0a0a0a',
  logoUrl: null,
  restaurantName: null,
};

export function BrandProvider({ children }) {
  const [brand, setBrand] = useState(() => {
    // Restore from cookies on init
    const saved = Cookies.get('brand_theme');
    if (saved) {
      try { return { ...DEFAULT_THEME, ...JSON.parse(saved) }; } catch {}
    }
    return DEFAULT_THEME;
  });

  // Apply CSS variables whenever brand changes
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', brand.primaryColor || DEFAULT_THEME.primaryColor);
    root.style.setProperty('--brand-secondary', brand.secondaryColor || DEFAULT_THEME.secondaryColor);
  }, [brand.primaryColor, brand.secondaryColor]);

  const loadBrand = useCallback(async (merchantId) => {
    if (!merchantId) return;
    try {
      const [profileRes, settingsRes] = await Promise.all([
        getMerchantProfile(merchantId),
        getMerchantSettings(merchantId),
      ]);

      const restaurant = profileRes?.data?.restaurant;
      const settings = settingsRes?.data?.settings;

      const newBrand = {
        primaryColor: settings?.primary_color || restaurant?.primary_color || DEFAULT_THEME.primaryColor,
        secondaryColor: settings?.secondary_color || restaurant?.secondary_color || DEFAULT_THEME.secondaryColor,
        logoUrl: restaurant?.logo_url || null,
        restaurantName: restaurant?.name || null,
      };

      setBrand(newBrand);
      Cookies.set('brand_theme', JSON.stringify(newBrand), { expires: 30 });
    } catch (err) {
      console.warn('Brand load failed:', err?.message);
    }
  }, []);

  const clearBrand = useCallback(() => {
    setBrand(DEFAULT_THEME);
    Cookies.remove('brand_theme');
  }, []);

  return (
    <BrandContext.Provider value={{ brand, loadBrand, clearBrand }}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrand must be used within BrandProvider');
  return ctx;
}
